import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useMixpanel } from 'src/utils/tracking/use-mixpanel';

import { useAppDispatch } from 'src/store/store';
import { authApi, useGetAuthContextQuery } from 'src/services/auth/auth.service';
import { OrganizationStatus } from 'src/services/organisation/organisation.types';

import { SplashScreen } from 'src/components/loading-screen';

import { TenantType } from 'src/types/enums';

import { logOut } from '../../utils/auth/utils';
// ----------------------------------------------------------------------

export const AccessTokenKey = 'access_token';

type Props = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: Props) {
  const { isLoading } = useGetAuthContextQuery(undefined);

  // JT1-1509: latch on the first settle instead of rendering straight off `isLoading`.
  // `isLoading` is true whenever the query is pending with no data, which is every retry of a
  // failing /auth/me. Rendering the splash on it unmounted <Container>, and remounting re-armed
  // `refetchOnMountOrArgChange`, so a single 401 spun up an unbounded fetch/unmount loop that
  // locked the user out. Once the query has settled once, <Container> stays mounted and owns
  // the redirect.
  const [hasSettled, setHasSettled] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setHasSettled(true);
    }
  }, [isLoading]);

  return <>{hasSettled ? <Container>{children}</Container> : <SplashScreen />}</>;
}

// ----------------------------------------------------------------------

function Container({ children }: Props) {
  const router = useRouter();

  const dispatch = useAppDispatch();

  const { identify } = useMixpanel();

  const { data, isLoading, isError } = useGetAuthContextQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: 30,
  });

  // NOTE (JT1-1509): do NOT invalidate the auth cache here on `isError`. Invalidating because the
  // auth query failed re-triggers the very query that failed: 401 -> invalidate -> refetch ->
  // 401 -> ... an unbounded loop that never self-recovers, so a transient 401 became a permanent
  // lockout. `check()` below already handles the error path (invalidate + resetApiState + logOut
  // + redirect), which is the one place that recovery belongs.

  const [checked, setChecked] = useState(false);

  const check = useCallback(async () => {
    if (isLoading) return;

    if (!data) {
      const searchParams = new URLSearchParams({
        returnTo: window.location.pathname,
      }).toString();

      const loginPath = paths.auth.login;

      const href = `${loginPath}?${searchParams}`;

      router.replace(href);
    }

    if (data && isError) {
      const searchParams = new URLSearchParams({
        returnTo: window.location.pathname,
      }).toString();

      dispatch(authApi.util.invalidateTags(['AuthContext']));
      await dispatch(authApi.util.resetApiState());
      logOut();

      const loginPath = paths.auth.login;

      const href = `${loginPath}?${searchParams}`;

      router.replace(href);
    } else if (data) {
      const { user, account, organization } = data;

      if (!user.email_verified) {
        router.replace(paths.auth.register.verify);
      } else if (
        account?.type !== TenantType.Candidate &&
        organization?.status === OrganizationStatus.INACTIVE
      ) {
        router.replace(paths.auth.register.organisation);
      }
    }

    identify(data?.account.id, {
      $first_name: data?.user.first_name,
      $last_name: data?.user.last_name,
      $name: data?.user.name,
      $email: data?.user.email,
      $phone: data?.user.phone,
      $role: data?.account.org_role,
      type: data?.account.type,
    });

    setChecked(true);
  }, [isLoading, data, isError, dispatch, router, identify]);

  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isError]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}
